import React, { useState } from 'react';
import './App.css';
import logo from './logo.svg';

function App() {
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [email, setEmail] = useState('');

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateCardName = (name) => {
    if (!name) return 'Cardholder name is required';
    if (name.length < 3) return 'Please enter full name';
    if (!/^[a-zA-Z\s]+$/.test(name)) return 'Only letters and spaces allowed';
    return '';
  };

  const validateCardNumber = (number) => {
    const cleaned = number.replace(/\s/g, '');
    if (!cleaned) return 'Card number is required';
    if (cleaned.length < 13) return 'Card number must be at least 13 digits';
    if (cleaned.length > 19) return 'Card number is too long';

    // Luhn algorithm
    let sum = 0;
    let isEven = false;
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned.charAt(i), 10);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    if (sum % 10 !== 0) return 'Invalid card number';
    return '';
  };

  const validateExpiry = (expiry) => {
    if (!expiry) return 'Expiry date is required';
    if (expiry.length !== 5) return 'Format: MM/YY';

    const [month, year] = expiry.split('/');
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt('20' + year, 10);

    if (monthNum < 1 || monthNum > 12) return 'Invalid month';

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
      return 'Card has expired';
    }

    return '';
  };

  const validateCvv = (cvv) => {
    if (!cvv) return 'CVV is required';
    if (cvv.length < 3) return 'CVV must be 3-4 digits';
    return '';
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.slice(0, 2) + '/' + v.slice(2, 4);
    }
    return v;
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });

    let error = '';
    switch (field) {
      case 'email':
        error = validateEmail(email);
        break;
      case 'cardName':
        error = validateCardName(cardName);
        break;
      case 'cardNumber':
        error = validateCardNumber(cardNumber);
        break;
      case 'expiryDate':
        error = validateExpiry(expiryDate);
        break;
      case 'cvv':
        error = validateCvv(cvv);
        break;
      default:
        break;
    }

    setErrors({ ...errors, [field]: error });
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.length <= 19) {
      setCardNumber(formatted);
      if (touched.cardNumber) {
        setErrors({ ...errors, cardNumber: validateCardNumber(formatted) });
      }
    }
  };

  const handleExpiryChange = (e) => {
    const formatted = formatExpiry(e.target.value);
    if (formatted.length <= 5) {
      setExpiryDate(formatted);
      if (touched.expiryDate) {
        setErrors({ ...errors, expiryDate: validateExpiry(formatted) });
      }
    }
  };

  const handleCvvChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/gi, '');
    if (value.length <= 4) {
      setCvv(value);
      if (touched.cvv) {
        setErrors({ ...errors, cvv: validateCvv(value) });
      }
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (touched.email) {
      setErrors({ ...errors, email: validateEmail(e.target.value) });
    }
  };

  const handleCardNameChange = (e) => {
    setCardName(e.target.value);
    if (touched.cardName) {
      setErrors({ ...errors, cardName: validateCardName(e.target.value) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {
      email: true,
      cardName: true,
      cardNumber: true,
      expiryDate: true,
      cvv: true
    };
    setTouched(allTouched);

    // Validate all fields
    const newErrors = {
      email: validateEmail(email),
      cardName: validateCardName(cardName),
      cardNumber: validateCardNumber(cardNumber),
      expiryDate: validateExpiry(expiryDate),
      cvv: validateCvv(cvv)
    };

    setErrors(newErrors);

    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some(error => error !== '');

    if (!hasErrors) {
      setIsProcessing(true);

      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2500));

      setIsProcessing(false);
      setPaymentSuccess(true);
    }
  };

  const handleReset = () => {
    setPaymentSuccess(false);
    setCardNumber('');
    setCardName('');
    setExpiryDate('');
    setCvv('');
    setEmail('');
    setTouched({});
    setErrors({});
  };

  return (
    <div className="payment-container">
      <header className="app-header">
        <img src={logo} alt="Logo" className="app-logo" />
      </header>
      <div className="payment-wrapper">
        <div className="payment-header">
          <h1>Complete Your Payment</h1>
          <p>Secure checkout powered by industry-leading encryption</p>
        </div>

        <div className="payment-content">
          <div className="order-summary">
            <h2>Order Summary</h2>
            <div className="summary-item">
              <span>Premium Plan</span>
              <span>$29.99</span>
            </div>
            <div className="summary-item">
              <span>Processing Fee</span>
              <span>$2.00</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-total">
              <span>Total</span>
              <span>$31.99</span>
            </div>
            <div className="security-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              <span>Secure 256-bit SSL encryption</span>
            </div>
          </div>

          <div className="payment-form-container">
            {paymentSuccess ? (
              <div className="success-message">
                <div className="success-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h2>Payment Successful!</h2>
                <p>Your transaction has been processed successfully.</p>
                <p className="transaction-id">Transaction ID: TXN{Date.now()}</p>
                <button onClick={handleReset} className="reset-button">
                  Make Another Payment
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="payment-form">
                <div className={`form-group ${errors.email && touched.email ? 'has-error' : ''} ${email && !errors.email && touched.email ? 'has-success' : ''}`}>
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={() => handleBlur('email')}
                    placeholder="your.email@example.com"
                    className={touched.email && errors.email ? 'error' : touched.email && !errors.email ? 'success' : ''}
                  />
                  {touched.email && errors.email && <span className="error-message">{errors.email}</span>}
                  {touched.email && !errors.email && email && (
                    <span className="success-icon-input">✓</span>
                  )}
                </div>

                <div className={`form-group ${errors.cardName && touched.cardName ? 'has-error' : ''} ${cardName && !errors.cardName && touched.cardName ? 'has-success' : ''}`}>
                  <label htmlFor="cardName">Cardholder Name</label>
                  <input
                    type="text"
                    id="cardName"
                    value={cardName}
                    onChange={handleCardNameChange}
                    onBlur={() => handleBlur('cardName')}
                    placeholder="John Doe"
                    className={touched.cardName && errors.cardName ? 'error' : touched.cardName && !errors.cardName ? 'success' : ''}
                  />
                  {touched.cardName && errors.cardName && <span className="error-message">{errors.cardName}</span>}
                  {touched.cardName && !errors.cardName && cardName && (
                    <span className="success-icon-input">✓</span>
                  )}
                </div>

                <div className={`form-group ${errors.cardNumber && touched.cardNumber ? 'has-error' : ''} ${cardNumber && !errors.cardNumber && touched.cardNumber ? 'has-success' : ''}`}>
                  <label htmlFor="cardNumber">Card Number</label>
                  <div className="input-with-icon">
                    <input
                      type="text"
                      id="cardNumber"
                      value={cardNumber}
                      onChange={handleCardNumberChange}
                      onBlur={() => handleBlur('cardNumber')}
                      placeholder="1234 5678 9012 3456"
                      className={touched.cardNumber && errors.cardNumber ? 'error' : touched.cardNumber && !errors.cardNumber ? 'success' : ''}
                    />
                    <div className="card-icons">
                      <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                        <rect width="32" height="20" rx="3" fill="#1434CB"/>
                        <circle cx="12" cy="10" r="6" fill="#EB001B"/>
                        <circle cx="20" cy="10" r="6" fill="#F79E1B" fillOpacity="0.8"/>
                      </svg>
                    </div>
                    {touched.cardNumber && !errors.cardNumber && cardNumber && (
                      <span className="success-icon-card">✓</span>
                    )}
                  </div>
                  {touched.cardNumber && errors.cardNumber && <span className="error-message">{errors.cardNumber}</span>}
                </div>

                <div className="form-row">
                  <div className={`form-group ${errors.expiryDate && touched.expiryDate ? 'has-error' : ''} ${expiryDate && !errors.expiryDate && touched.expiryDate ? 'has-success' : ''}`}>
                    <label htmlFor="expiryDate">Expiry Date</label>
                    <input
                      type="text"
                      id="expiryDate"
                      value={expiryDate}
                      onChange={handleExpiryChange}
                      onBlur={() => handleBlur('expiryDate')}
                      placeholder="MM/YY"
                      className={touched.expiryDate && errors.expiryDate ? 'error' : touched.expiryDate && !errors.expiryDate ? 'success' : ''}
                    />
                    {touched.expiryDate && errors.expiryDate && <span className="error-message">{errors.expiryDate}</span>}
                    {touched.expiryDate && !errors.expiryDate && expiryDate && (
                      <span className="success-icon-input">✓</span>
                    )}
                  </div>

                  <div className={`form-group ${errors.cvv && touched.cvv ? 'has-error' : ''} ${cvv && !errors.cvv && touched.cvv ? 'has-success' : ''}`}>
                    <label htmlFor="cvv">CVV</label>
                    <input
                      type="text"
                      id="cvv"
                      value={cvv}
                      onChange={handleCvvChange}
                      onBlur={() => handleBlur('cvv')}
                      placeholder="123"
                      className={touched.cvv && errors.cvv ? 'error' : touched.cvv && !errors.cvv ? 'success' : ''}
                    />
                    {touched.cvv && errors.cvv && <span className="error-message">{errors.cvv}</span>}
                    {touched.cvv && !errors.cvv && cvv && (
                      <span className="success-icon-input">✓</span>
                    )}
                  </div>
                </div>

                <button type="submit" className="pay-button" disabled={isProcessing}>
                  {isProcessing ? (
                    <>
                      <span className="spinner"></span>
                      Processing...
                    </>
                  ) : (
                    'Pay $31.99'
                  )}
                </button>

                <p className="payment-terms">
                  By confirming your payment, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
